import { PERMISSION_MOD_SELECT_ALL_ID } from "@/constants/mod-perms-tool.constants";
import { COLORS } from "@/constants/mod-perms-tool.constants";
import {
  GrantedPermission,
  DelegateContext,
  Permission,
  ModifiedPermissionsRequest,
  PermModOperation,
  PlatformEnum,
  type ToggledResponse,
  ChainMode,
  type DelegateAddType,
  type SocialAccountQueryParamStructure,
} from "@/types/modular-permissions.types";
import { HexString } from "@/types/general.types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

interface ModularPermissionsStoreType {
  activeDelegateContext: string;
  pendingCreations: HexString[];
  mirroredDelegatePermissions: Permission[];
  mirroredReceivedPermissions: Permission[];
  delegatePermissions: GrantedPermission[];
  receivedPermissions: GrantedPermission[];
  isDelegateBeingCreated: boolean;
  permModRecords: ModifiedPermissionsRequest[];
  activeChainMode: ChainMode | undefined;
  setActiveChainMode: (mode: ChainMode) => void;
  setActiveDelegateContext: (value: DelegateContext) => void;
  isDelegateContextGranted: () => boolean;
  setMirroredDelegatePermissions: (delegates: Permission[]) => void;
  setMirroredReceivedPermissions: (vaults: Permission[]) => void;
  setDelegatePermissions: (delegates: Permission[]) => void;
  setReceivedPermissions: (permissions: Permission[]) => void;
  removeReceivedPermission: (vaultAddress: HexString) => void;
  clearRecievedPermissions: () => void;
  addDelegation: (delegate: DelegateAddType) => void;
  removeDelegate: (walletAddress: HexString) => void;
  clearDelegates: () => void;
  toggleOffPermission: (walletAddress: HexString, permissionId: number) => void;
  toggleOnPermission: (walletAddress: HexString, permissionId: number) => void;
  isAllPermissionsSelected: (walletAddress: HexString) => boolean;
  isChecked: (
    walletAddress: HexString,
    permissionId: number,
  ) => boolean | undefined;
  startDelegateCreation: () => void;
  cancelDelegateCreation: () => void;
  isAlreadyDelegated: (walletAddress: HexString) => boolean;
  updatePermModRecord: (
    vaultAddress: HexString,
    delegateAddress: HexString,
    permissionId: number,
    operation: PermModOperation,
  ) => void;
  clearPermModRecords: () => void;
  removePermModRecordsForDelegate: (delegateAddress: HexString) => void;
  clearPendingCreations: () => void;
  removeFromPendingCreations: (address: HexString) => void;
  addToPendingCreations: (address: HexString) => void;
  getToggledPermissions: () => ToggledResponse;
  socialDelegates: DelegateAddType | undefined;
  addSocialDelegate: (delegate: DelegateAddType | undefined) => void;
  socialDelegateQueryParams: SocialAccountQueryParamStructure | undefined;
  setSocialDelegateQueryParams: (
    params: SocialAccountQueryParamStructure,
  ) => void;
}

export const useModularPermissionsStore = create<ModularPermissionsStoreType>()(
  immer((set, get) => ({
    socialDelegates: undefined,
    addSocialDelegate: (delegate) => set({ socialDelegates: delegate }),
    socialDelegateQueryParams: undefined,
    setSocialDelegateQueryParams: (params) =>
      set({ socialDelegateQueryParams: params }),
    activeChainMode: undefined,
    setActiveChainMode: (mode) => set({ activeChainMode: mode }),
    activeDelegateContext: DelegateContext.Granted,
    setActiveDelegateContext: (context) =>
      set({ activeDelegateContext: context }),
    isDelegateContextGranted: () =>
      get().activeDelegateContext === DelegateContext.Granted,
    permModRecords: [],
    updatePermModRecord: (
      vaultAddress,
      delegateAddress,
      permissionId,
      operation,
    ) =>
      set((state) => {
        const oppositeOperation =
          operation === PermModOperation.Add
            ? PermModOperation.Remove
            : PermModOperation.Add;
        const delegate = state.delegatePermissions.find(
          (d) => d.delegate === delegateAddress,
        );
        if (!delegate) return;

        // Update the allPermissionsEnabled property for the delegate
        if (permissionId === PERMISSION_MOD_SELECT_ALL_ID) {
          delegate.allPermissionsEnabled = operation === PermModOperation.Add;
        }

        // Get the current and opposite operation records for the selected delegate
        const currentOperationRecord = state.permModRecords.find(
          (record) =>
            record.operation === operation &&
            record.delegate === delegateAddress,
        );
        const oppositeOperationRecord = state.permModRecords.find(
          (record) =>
            record.operation === oppositeOperation &&
            record.delegate === delegateAddress,
        );

        // If we are adding a permission, remove it from the opposite operation record if its there
        if (oppositeOperationRecord) {
          const id = oppositeOperationRecord.ids.indexOf(permissionId);
          if (id !== -1) {
            // Remove the permissionId from opposite operation
            oppositeOperationRecord.ids.splice(id, 1);
            // Remove the opposite operation record if no ids are left
            if (oppositeOperationRecord.ids.length === 0) {
              state.permModRecords = state.permModRecords.filter(
                (record) =>
                  !(
                    record.delegate === delegateAddress &&
                    record.operation === oppositeOperation
                  ),
              );
            }
            return;
          }
        }
        // Now handle the current operation, adding the permissionId if necessary
        if (currentOperationRecord) {
          // If current operation record exists, add the permissionId if not already present
          if (!currentOperationRecord.ids.includes(permissionId)) {
            currentOperationRecord.ids.push(permissionId);
          }
        } else {
          // If current operation record doesn't exist, create it and add permissionId
          state.permModRecords.push({
            delegate: delegateAddress,
            vault: vaultAddress,
            operation: operation,
            ids: [permissionId],
            isSocialAccount: delegate.isSocialAccount,
            platform: delegate.platform,
            userId: delegate.userId,
          });
        }
      }),

    clearPermModRecords: () => set({ permModRecords: [] }),
    removePermModRecordsForDelegate: (delegateAddress) =>
      set((state) => ({
        permModRecords: state.permModRecords.filter(
          (record) => record.delegate !== delegateAddress,
        ),
      })),
    mirroredDelegatePermissions: [],
    setMirroredDelegatePermissions: (delegates) =>
      set({ mirroredDelegatePermissions: delegates }),
    mirroredReceivedPermissions: [],
    setMirroredReceivedPermissions: (vaults) =>
      set({ mirroredReceivedPermissions: vaults }),
    delegatePermissions: [],
    setDelegatePermissions: (delegates) =>
      set({
        delegatePermissions: delegates.map((delegate, index) => ({
          ...delegate,
          allPermissionsEnabled: delegate.permissionIds.includes(
            PERMISSION_MOD_SELECT_ALL_ID,
          ),
          backgroundColor: COLORS[index % COLORS.length],
        })),
      }),
    receivedPermissions: [],
    setReceivedPermissions: (permissions) =>
      set({
        receivedPermissions: permissions.map((delegate, index) => ({
          ...delegate,
          allPermissionsEnabled: delegate.permissionIds.includes(
            PERMISSION_MOD_SELECT_ALL_ID,
          ),
          backgroundColor: COLORS[index % COLORS.length],
        })),
      }),
    clearRecievedPermissions: () => set({ receivedPermissions: [] }),
    removeReceivedPermission: (vaultAddress) =>
      set((state) => ({
        receivedPermissions: state.receivedPermissions.filter(
          (permission) => permission.vault !== vaultAddress,
        ),
      })),
    removeDelegate: (walletAddress) =>
      set((state) => {
        state.delegatePermissions = state.delegatePermissions.filter(
          (delegate) => delegate.delegate !== walletAddress,
        );
        state.permModRecords = state.permModRecords.filter(
          (perm) => perm.delegate !== walletAddress,
        );
      }),
    clearDelegates: () => set({ delegatePermissions: [] }),
    toggleOnPermission: (walletAddress, permissionId) =>
      set((state) => {
        const delegate = state.delegatePermissions.find(
          (d) => d.delegate === walletAddress,
        );
        // Add the permission if it doesn't already exist
        if (delegate && !delegate.permissionIds.includes(permissionId))
          delegate.permissionIds.push(permissionId);
      }),
    toggleOffPermission: (walletAddress, permissionId) =>
      set((state) => {
        const delegate = state.delegatePermissions.find(
          (d) => d.delegate === walletAddress,
        );
        if (delegate) {
          const permissionIndex = delegate.permissionIds.findIndex(
            (permission) => permission === permissionId,
          );

          // Remove the permission if it exists
          if (permissionIndex !== -1)
            delegate.permissionIds.splice(permissionIndex, 1);
        }
      }),
    isAllPermissionsSelected: (walletAddress) => {
      const delegate = get().delegatePermissions.find(
        (delegate) => delegate.delegate === walletAddress,
      );
      if (delegate) return delegate.allPermissionsEnabled;
      else return false;
    },
    isChecked: (walletAddress, permissionId) => {
      const delegate = get().delegatePermissions.find(
        (d) => d.delegate === walletAddress,
      );
      if (delegate)
        return delegate.permissionIds.find(
          (permission) => permission === permissionId,
        )
          ? true
          : false;
    },
    isDelegateBeingCreated: false,
    startDelegateCreation: () => set({ isDelegateBeingCreated: true }),
    cancelDelegateCreation: () => set({ isDelegateBeingCreated: false }),
    addDelegation: ({
      vault,
      walletAddress,
      isSocialAccount = false,
      platform,
      userId,
    }) =>
      set((state) => {
        // Check if the delegate already exists
        const exists = state.delegatePermissions.some(
          (permission) =>
            permission.delegate === walletAddress &&
            permission.vault === vault &&
            permission.isSocialAccount === isSocialAccount &&
            permission.userId === userId &&
            permission.platform === platform,
        );

        // If the delegate already exists, do nothing
        if (exists) {
          return state;
        }

        // Determine background color
        let color = COLORS[state.delegatePermissions.length % COLORS.length];

        // Avoid duplicate colors for neighboring entries
        if (state.delegatePermissions.length) {
          if (color === state.delegatePermissions[0].backgroundColor) {
            color = COLORS.filter((c) => c !== color)[0];
          }
        }

        // Add a default permission request to the list
        state.permModRecords.push({
          operation: PermModOperation.Add,
          vault: vault,
          delegate: walletAddress,
          ids: [PERMISSION_MOD_SELECT_ALL_ID],
          isSocialAccount,
          platform,
          userId,
        });

        // Disable creation card
        state.isDelegateBeingCreated = false;

        // Add the new delegate to the beginning of the selected delegates array
        state.delegatePermissions.unshift({
          delegate: walletAddress,
          vault: vault,
          permissionIds: [PERMISSION_MOD_SELECT_ALL_ID],
          allPermissionsEnabled: true,
          backgroundColor: color,
          isSocialAccount,
          userId,
          platform,
        });
      }),
    isAlreadyDelegated: (walletAddress) => {
      const isDelegated = get().delegatePermissions.find(
        (delegate) => delegate.delegate === walletAddress,
      );
      return isDelegated ? true : false;
    },
    pendingCreations: [],
    clearPendingCreations: () => set({ pendingCreations: [] }),
    removeFromPendingCreations: (address) =>
      set((state) => ({
        pendingCreations: state.pendingCreations.filter(
          (add) => add !== address,
        ),
      })),
    addToPendingCreations: (address) =>
      set((state) => ({
        pendingCreations: [...state.pendingCreations, address],
      })),
    getToggledPermissions: () =>
      get().isDelegateContextGranted()
        ? {
            blessnetPermissions: get().delegatePermissions,
            mirroredPermissions: get().mirroredDelegatePermissions,
          }
        : {
            blessnetPermissions: get().receivedPermissions,
            mirroredPermissions: get().mirroredReceivedPermissions,
          },
  })),
);
